import React from "react";
import PageLayout from "../components/PageLayout";
import RAGBadge from "../components/RAGBadge";
import { useTheme } from "../DataContext";
import { DEMO_PROJECTS, DEMO_RISKS, DEMO_PORTFOLIO_KPIs } from "../demoData";
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer,
} from "recharts";

const fmt = (n) => n >= 1000000 ? `$${(n/1000000).toFixed(1)}M` : `$${(n/1000).toFixed(0)}K`;

export default function ExecutiveBoardView() {
  const { theme } = useTheme();
  const kpi = DEMO_PORTFOLIO_KPIs;

  const topRisks      = [...DEMO_RISKS].sort((a, b) => b.score - a.score).slice(0, 5);
  const criticalProjs = DEMO_PROJECTS.filter(p => p.rag === "red").slice(0, 3);
  const investProjs   = DEMO_PROJECTS.filter(p => p.cpi >= 1.05 && p.failProb < 15).slice(0, 3);

  const ragDist = [
    { name: "On Track",  value: kpi.onTrack,  color: theme.success },
    { name: "At Risk",   value: kpi.atRisk,   color: theme.warning },
    { name: "Failing",   value: kpi.failing,  color: theme.danger  },
  ];

  const benefitsSummary = [
    { q: "Q1 2026", gap: 420000,   confidence: 65 },
    { q: "Q2 2026", gap: 180000,   confidence: 78 },
    { q: "Q3 2026", gap: 820000,   confidence: 52 },
    { q: "Q4 2026", gap: -300000,  confidence: 88 },
  ];

  const budgetSummary = [
    { prog: "Digital Transf.",   budget: 15700000, spent: 7000000 },
    { prog: "Risk & Compliance", budget: 2080000,  spent: 1550000 },
    { prog: "Customer Exp.",     budget: 1780000,  spent: 1240000 },
    { prog: "Operations",        budget: 1900000,  spent: 450000  },
    { prog: "IT Operations",     budget: 2100000,  spent: 1920000 },
    { prog: "People & Culture",  budget: 750000,   spent: 680000  },
  ];

  const now = new Date("2026-05-11").toLocaleDateString("en-US", { day: "2-digit", month: "long", year: "numeric" });

  return (
    <PageLayout
      title="Executive Board View"
      subtitle={`Eagle-eye portfolio dashboard for board-level consumption — P-11 · As at ${now}`}
    >
      {/* Board Classification Banner */}
      <div style={{
        padding: "10px 20px", borderRadius: "8px", marginBottom: "20px",
        background: `linear-gradient(90deg, ${theme.accent}18, transparent)`,
        border: `1px solid ${theme.accent}33`,
        display: "flex", justifyContent: "space-between", alignItems: "center",
      }}>
        <div style={{ fontFamily: "'Orbitron',monospace", fontSize: "11px", color: theme.accent, letterSpacing: "2px" }}>
          BOARD CONFIDENTIAL — PORTFOLIO INTELLIGENCE SUMMARY
        </div>
        <div style={{ fontSize: "11px", color: theme.textMuted, fontFamily: "'Share Tech Mono',monospace" }}>
          ML Engine: ACTIVE · Data Quality: {kpi.pmDataQuality}% · Models: RF + XGB + LightGBM
        </div>
      </div>

      {/* Top KPIs — Board Level */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "16px", marginBottom: "20px" }}>
        <div className="pmo-card" style={{ borderLeft: `4px solid ${theme.accent}`, padding: "20px 24px" }}>
          <div style={{ fontSize: "11px", color: theme.textMuted, letterSpacing: "1px", textTransform: "uppercase", marginBottom: "8px" }}>Portfolio Investment</div>
          <div style={{ fontFamily: "'Orbitron',monospace", fontSize: "32px", fontWeight: 900, color: theme.accent }}>{fmt(kpi.totalBudget)}</div>
          <div style={{ fontSize: "12px", color: theme.textMuted, marginTop: "4px" }}>Spent: {fmt(kpi.totalSpent)} · {Math.round(kpi.totalSpent/kpi.totalBudget*100)}% consumed</div>
        </div>
        <div className="pmo-card" style={{ borderLeft: `4px solid ${kpi.avgFailProb > 35 ? theme.danger : theme.warning}`, padding: "20px 24px" }}>
          <div style={{ fontSize: "11px", color: theme.textMuted, letterSpacing: "1px", textTransform: "uppercase", marginBottom: "8px" }}>Portfolio Risk Score</div>
          <div style={{ fontFamily: "'Orbitron',monospace", fontSize: "32px", fontWeight: 900, color: kpi.avgFailProb > 35 ? theme.danger : theme.warning }}>
            {kpi.avgFailProb}%
          </div>
          <div style={{ fontSize: "12px", color: theme.textMuted, marginTop: "4px" }}>Avg failure probability across {kpi.totalProjects} projects</div>
        </div>
        <div className="pmo-card" style={{ borderLeft: `4px solid ${theme.success}`, padding: "20px 24px" }}>
          <div style={{ fontSize: "11px", color: theme.textMuted, letterSpacing: "1px", textTransform: "uppercase", marginBottom: "8px" }}>Benefits Pipeline</div>
          <div style={{ fontFamily: "'Orbitron',monospace", fontSize: "32px", fontWeight: 900, color: theme.success }}>{fmt(kpi.totalBenefitsProjected)}</div>
          <div style={{ fontSize: "12px", color: theme.textMuted, marginTop: "4px" }}>vs. {fmt(kpi.totalBenefitsPlanned)} planned · Gap: {fmt(kpi.totalBenefitsPlanned - kpi.totalBenefitsProjected)}</div>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "200px 1fr 1fr", gap: "20px", marginBottom: "20px" }}>
        {/* RAG Donut */}
        <div className="pmo-card">
          <div className="section-title">Portfolio RAG</div>
          <ResponsiveContainer width="100%" height={130}>
            <PieChart>
              <Pie data={ragDist} cx="50%" cy="50%" innerRadius={35} outerRadius={55} paddingAngle={3} dataKey="value">
                {ragDist.map((e, i) => <Cell key={i} fill={e.color} />)}
              </Pie>
              <Tooltip contentStyle={{ background: theme.card, border: `1px solid ${theme.cardBorder}`, borderRadius: "8px" }} />
            </PieChart>
          </ResponsiveContainer>
          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            {ragDist.map(r => (
              <div key={r.name} style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ fontSize: "10px", color: theme.textMuted }}>{r.name}</span>
                <span style={{ fontFamily: "'Orbitron',monospace", fontSize: "14px", color: r.color, fontWeight: 700 }}>{r.value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Budget by Programme */}
        <div className="pmo-card">
          <div className="section-title">Investment by Programme</div>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={budgetSummary} layout="vertical" margin={{ top: 0, right: 10, bottom: 0, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={`${theme.cardBorder}44`} horizontal={false} />
              <XAxis type="number" tickFormatter={v => fmt(v)} tick={{ fill: theme.textMuted, fontSize: 9 }} />
              <YAxis type="category" dataKey="prog" tick={{ fill: theme.textSecondary, fontSize: 9 }} width={95} />
              <Tooltip formatter={v => fmt(v)} contentStyle={{ background: theme.card, border: `1px solid ${theme.cardBorder}`, borderRadius: "8px" }} />
              <Bar dataKey="budget" fill={`${theme.textMuted}44`} name="Budget"   radius={[0,4,4,0]} />
              <Bar dataKey="spent"  fill={theme.accent}            name="Actuals"  radius={[0,4,4,0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Benefits Gap Forecast */}
        <div className="pmo-card">
          <div className="section-title">Benefits Gap Forecast 2026</div>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={benefitsSummary} margin={{ top: 5, right: 10, bottom: 0, left: -10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={`${theme.cardBorder}66`} />
              <XAxis dataKey="q" tick={{ fill: theme.textMuted, fontSize: 10 }} />
              <YAxis tickFormatter={v => fmt(Math.abs(v))} tick={{ fill: theme.textMuted, fontSize: 9 }} />
              <Tooltip formatter={v => fmt(v)} contentStyle={{ background: theme.card, border: `1px solid ${theme.cardBorder}`, borderRadius: "8px" }} />
              <Bar dataKey="gap" name="Benefits Gap" radius={[4,4,0,0]}>
                {benefitsSummary.map((d, i) => <Cell key={i} fill={d.gap > 0 ? theme.danger : theme.success} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Critical Decisions Panel */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "20px", marginBottom: "20px" }}>
        {/* Top 5 Risks */}
        <div className="pmo-card">
          <div className="section-title">Top 5 Risks Requiring Board Attention</div>
          {topRisks.map((r, i) => (
            <div key={r.id} style={{
              padding: "10px 12px", marginBottom: "6px",
              background: theme.bg, borderRadius: "6px",
              borderLeft: `3px solid ${r.score >= 50 ? theme.danger : theme.warning}`,
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: "12px", color: theme.textPrimary, fontWeight: 500 }}>{r.title}</span>
                <span style={{ fontFamily: "'Orbitron',monospace", fontSize: "13px", color: r.score >= 50 ? theme.danger : theme.warning, fontWeight: 700 }}>{r.score.toFixed(0)}</span>
              </div>
              <div style={{ fontSize: "10px", color: theme.textMuted, marginTop: "2px" }}>
                {r.project} · Exposure: {fmt(r.budget_exposure)}
              </div>
            </div>
          ))}
        </div>

        {/* Projects Requiring Intervention */}
        <div className="pmo-card">
          <div className="section-title">Projects Requiring Board Decision</div>
          {criticalProjs.length > 0 ? criticalProjs.map(p => (
            <div key={p.id} style={{
              padding: "10px 12px", marginBottom: "8px",
              background: `${theme.danger}08`, borderRadius: "6px",
              border: `1px solid ${theme.danger}33`,
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <div>
                  <div style={{ fontSize: "12px", color: theme.textPrimary, fontWeight: 600 }}>{p.name}</div>
                  <div style={{ fontSize: "10px", color: theme.textMuted, marginTop: "2px" }}>
                    CPI: {p.cpi.toFixed(2)} · Fail Risk: {p.failProb}%
                  </div>
                </div>
                <RAGBadge status="red" />
              </div>
              <div style={{ fontSize: "11px", color: theme.danger, marginTop: "6px", fontWeight: 500 }}>
                Recommendation: {p.failProb > 70 ? "Consider termination — escalate to board" : "Immediate intervention — assign executive sponsor"}
              </div>
            </div>
          )) : (
            <div style={{ fontSize: "12px", color: theme.textMuted, padding: "8px" }}>No critical projects at this time</div>
          )}

          <div className="section-title" style={{ marginTop: "14px" }}>High-Value Opportunities</div>
          {investProjs.map(p => (
            <div key={p.id} style={{
              padding: "10px 12px", marginBottom: "8px",
              background: `${theme.success}08`, borderRadius: "6px",
              border: `1px solid ${theme.success}33`,
            }}>
              <div style={{ fontSize: "12px", color: theme.textPrimary, fontWeight: 600 }}>{p.name}</div>
              <div style={{ fontSize: "10px", color: theme.textMuted }}>CPI: {p.cpi.toFixed(2)} · Fail Risk: {p.failProb}%</div>
              <div style={{ fontSize: "11px", color: theme.success, marginTop: "4px" }}>Consider accelerating investment</div>
            </div>
          ))}
        </div>

        {/* 3 Key Decisions Required */}
        <div className="pmo-card">
          <div className="section-title">3 Decisions Required This Quarter</div>
          {[
            {
              n: "01", urgency: "CRITICAL",
              title: "Cybersecurity Framework — Scope Reduction",
              desc: "P003 is 78% likely to fail at current trajectory. Recommend immediate scope reduction to Phase 1 deliverables only to preserve budget.",
              color: theme.danger,
            },
            {
              n: "02", urgency: "HIGH",
              title: "HR Digital Transformation — PM Replacement",
              desc: "P007 PM performance score is 56%. 55-day milestone slip. Recommend appointing co-PM or replacing PM to recover schedule.",
              color: "#FF6B35",
            },
            {
              n: "03", urgency: "MEDIUM",
              title: "AI/ML Platform — Investment Acceleration",
              desc: "P010 showing strong early indicators (CPI: 1.08, Fail Risk: 10%). Board approval requested to accelerate funding by $800K.",
              color: theme.cyan,
            },
          ].map(d => (
            <div key={d.n} style={{
              padding: "12px 14px", marginBottom: "10px",
              background: `${d.color}08`, borderRadius: "8px",
              borderLeft: `3px solid ${d.color}`,
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px" }}>
                <span style={{ fontFamily: "'Orbitron',monospace", fontSize: "11px", color: d.color, fontWeight: 700 }}>DECISION {d.n}</span>
                <span style={{ padding: "2px 8px", borderRadius: "10px", fontSize: "9px", fontWeight: 700, background: `${d.color}22`, color: d.color }}>{d.urgency}</span>
              </div>
              <div style={{ fontSize: "12px", color: theme.textPrimary, fontWeight: 600, marginBottom: "4px" }}>{d.title}</div>
              <div style={{ fontSize: "11px", color: theme.textMuted }}>{d.desc}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Executive Summary Row */}
      <div className="pmo-card" style={{ background: `linear-gradient(135deg, ${theme.accent}08, ${theme.card})` }}>
        <div className="section-title">AI-Generated Executive Narrative</div>
        <div style={{ fontSize: "13px", color: theme.textSecondary, lineHeight: "1.7" }}>
          The portfolio of <strong style={{ color: theme.textPrimary }}>12 active projects</strong> representing{" "}
          <strong style={{ color: theme.accent }}>{fmt(kpi.totalBudget)}</strong> in approved investment is currently performing at{" "}
          <strong style={{ color: kpi.avgCPI < 0.95 ? theme.danger : theme.warning }}>CPI {kpi.avgCPI.toFixed(3)}</strong> and{" "}
          <strong style={{ color: kpi.avgSPI < 0.95 ? theme.danger : theme.warning }}>SPI {kpi.avgSPI.toFixed(3)}</strong> against plan.
          {" "}<strong style={{ color: theme.danger }}>3 projects are in failing status</strong> with combined failure probability averaging {kpi.avgFailProb}%.
          The portfolio is projected to <strong style={{ color: kpi.totalBenefitsProjected < kpi.totalBenefitsPlanned ? theme.danger : theme.success }}>
            {kpi.totalBenefitsProjected < kpi.totalBenefitsPlanned ? "under-deliver" : "exceed"} planned benefits
          </strong> by {fmt(Math.abs(kpi.totalBenefitsPlanned - kpi.totalBenefitsProjected))}.
          {" "}Priority actions this quarter: (1) Board decision on P003/P007 intervention strategy,
          (2) Approval of P010 investment acceleration, (3) Review of PM performance remediation plan for 3 underperforming PMs.
          PM data quality at <strong style={{ color: kpi.pmDataQuality >= 85 ? theme.success : theme.warning }}>{kpi.pmDataQuality}%</strong>{" "}
          compliance — recommended target is 95%.
        </div>
      </div>
    </PageLayout>
  );
}
